import { type ReactNode, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import {
  animationFrameScheduler,
  filter,
  map,
  merge,
  mergeWith,
  NEVER,
  type Observable,
  observeOn,
  of,
  take,
} from 'rxjs'
import {
  ConversationLayout,
  type ConversationLayoutProps,
  type ScrollToBottomOptions,
} from '@/features/messages/components/ConversationLayout/index.tsx'
import type { ConversationViewModel } from '@/features/messages/ConversationViewModel'
import { toStream } from '@/utils/mobx-util'
import type { ChatListViewModel } from '@/features/messages/ChatListViewModel'

interface ConversationLayoutContainerProps<T, V, E> {
  /**
   * Backing view model for the conversation.
   */
  conversationVm: ConversationViewModel

  /**
   * The chat list view mode.
   */
  chatVm: ChatListViewModel<T, V, E>

  /** @see ConversationLayoutProps.children */
  children?: ReactNode

  /** @see ConversationLayoutProps.scrollingElement */
  scrollingElement?: HTMLElement | null

  /** @see ConversationLayoutProps.composerPositioning */
  composerPositioning?: ConversationLayoutProps['composerPositioning']

  /** @see ConversationLayoutProps.isComposerHidden */
  isComposerHidden?: ConversationLayoutProps['isComposerHidden']

  /** @see ConversationLayoutProps.composerMask */
  composerMask?: ConversationLayoutProps['composerMask']

  /** @see ConversationLayoutProps.spacing */
  spacing?: ConversationLayoutProps['spacing']

  /** @see ConversationLayoutProps.scrollToBottom$ */
  scrollToBottom$?: ConversationLayoutProps['scrollToBottom$']

  /** @see ConversationLayoutProps.messageList */
  messageList?: ReactNode

  /** @see ConversationLayoutProps.composer */
  composer?: ReactNode

  /** @see ConversationLayoutProps.typing */
  typing?: string[]

  /** @see ConversationLayoutProps.header */
  header?: ReactNode

  /**
   * If specified, will wait with performing scroll requests until this emits
   * `true`.
   */
  isReadyToScroll$?: Observable<boolean>
}

/**
 * Conversation Layout Container
 */
export const ConversationLayoutContainer = observer(
  function ConversationLayoutContainer<T, V, E>({
    conversationVm,
    chatVm,
    scrollingElement,
    composerPositioning,
    messageList,
    header,
    composer,
    isComposerHidden,
    composerMask,
    spacing,
    typing,
    scrollToBottom$: scrollToBottomProp$,
    isReadyToScroll$,
    children,
  }: ConversationLayoutContainerProps<T, V, E>) {
    const scrollOnMessagesLoaded$ = useMemo(
      () =>
        toStream(() => conversationVm.fetchMessages.resolved, true).pipe(
          filter(Boolean),
          take(1),
          mergeWith(
            of(true).pipe(
              // When the view mounts with messages already loaded,
              // scrolling immediately doesn't seem to work unless
              // we wait a frame.
              observeOn(animationFrameScheduler),
            ),
          ),
          map((): ScrollToBottomOptions => ({ behavior: 'instant' })),
        ),
      [conversationVm],
    )

    const scrollOnNewItem$ = useMemo(
      () =>
        chatVm.itemAdded.pipe(
          // Only when the item that was added is the last item in the list,
          // since that's when we want to scroll.
          filter((item) => chatVm.items[chatVm.items.length - 1] === item),
          map(
            (): ScrollToBottomOptions => ({
              behavior: 'smooth',
              onlyWhenAnchored: true,
            }),
          ),
        ),
      [chatVm],
    )

    const scrollToBottom$ = useMemo(
      () =>
        merge(
          scrollOnNewItem$,
          scrollOnMessagesLoaded$,
          scrollToBottomProp$ ?? NEVER,
        ),
      [scrollOnNewItem$, scrollOnMessagesLoaded$, scrollToBottomProp$],
    )

    return (
      <ConversationLayout
        header={header}
        typing={typing}
        scrollingElement={scrollingElement}
        scrollToBottom$={scrollToBottom$}
        isReadyToScroll$={isReadyToScroll$}
        composerPositioning={composerPositioning}
        messageList={messageList}
        composer={composer}
        isComposerHidden={isComposerHidden}
        composerMask={composerMask}
        spacing={spacing}
        scrollButtonLabel={
          conversationVm.unreadCount > 0
            ? `View ${conversationVm.unreadCount} unread`
            : 'Scroll to bottom'
        }
      >
        {children}
      </ConversationLayout>
    )
  },
)
